// Mapas de labels y clasificación de campos del detail de la bitácora.
// Un solo punto de verdad para todos los renderers: campo → key i18n, valor → key i18n,
// y los sets que gobiernan el formateo (moneda, %, períodos, métricas, ocultos).

export const FIELD_LABELS = {
    daily_limit_type: 'activity_log.field.daily_limit_type',
    daily_limit_currency: 'activity_log.field.daily_limit_currency',
    daily_limit_value: 'activity_log.field.daily_limit_value',
    // La regla de cruce con GA4 de una cuenta (AD_ACCOUNT_EDITED, tanda «Cruce GA4»).
    ga4_attribution: 'activity_log.field.ga4_attribution',
    business_type: 'activity_log.field.business_type',
    industry: 'activity_log.field.industry',
    primary_kpi: 'activity_log.field.primary_kpi',
    notes: 'activity_log.field.notes',
    external_chat_channel_id: 'activity_log.field.external_chat_channel_id',
    export_sheet_url_for_metrics: 'activity_log.field.export_sheet_url_for_metrics',
    export_sheet_url_for_indicators: 'activity_log.field.export_sheet_url_for_indicators',
    name: 'activity_log.field.name',
    budget_type: 'activity_log.field.budget_type',
    business_unit_type: 'activity_log.field.business_unit_type',
    budget_currency: 'activity_log.field.budget_currency',
    total_budget: 'activity_log.field.total_budget',
    allocated_budget: 'activity_log.field.allocated_budget',
    leftover_budget: 'activity_log.field.leftover_budget',
    rollover: 'activity_log.field.rollover',
    period_type: 'activity_log.field.period_type',
    campaign_channel: 'activity_log.field.campaign_channel',
    kind: 'activity_log.field.kind',
    account_name: 'activity_log.field.account_name',
    cost: 'activity_log.field.cost',
    budget: 'activity_log.field.budget',
    limit: 'activity_log.field.limit',
    currency: 'activity_log.field.currency',
    campaigns: 'activity_log.field.campaigns',
    daily_budget: 'activity_log.field.daily_budget',
    converted: 'activity_log.field.converted',
    indicator: 'activity_log.field.indicator',
    level: 'activity_log.field.level',
    metric_name: 'activity_log.field.metric_name',
    value: 'activity_log.field.value',
    goal_value: 'activity_log.field.goal_value',
    expected_value: 'activity_log.field.expected_value',
    fluctuation: 'activity_log.field.fluctuation',
    completion: 'activity_log.field.completion',
    alert_when_spent_over: 'activity_log.field.alert_when_spent_over',
    total_spent: 'activity_log.field.total_spent',
    time_completion: 'activity_log.field.time_completion',
    budget_completion: 'activity_log.field.budget_completion',
    previous_value: 'activity_log.field.previous_value',
    new_value: 'activity_log.field.new_value',
    today_ods: 'activity_log.field.today_ods',
    main_period: 'activity_log.field.main_period',
    vs_period: 'activity_log.field.vs_period',
    vs_period_value: 'activity_log.field.vs_period_value',
    item_type: 'activity_log.field.item_type',
    item_label: 'activity_log.field.item_label',
    remaining_days: 'activity_log.field.remaining_days',
    available_funds: 'activity_log.field.available_funds',
    channel: 'activity_log.field.channel',
    url: 'activity_log.field.url',
    previous_status: 'activity_log.field.previous_status',
    new_status: 'activity_log.field.new_status',
    campaign_name: 'activity_log.field.campaign_name',
    adgroup_name: 'activity_log.field.adgroup_name',
    report_title: 'activity_log.field.report_title',
    is_temporary: 'activity_log.field.is_temporary',
    status: 'activity_log.field.status',
    bid_strategy: 'activity_log.field.bid_strategy',
    priority: 'activity_log.field.priority',
    target: 'activity_log.field.target',
    before: 'activity_log.field.before',
    after: 'activity_log.field.after',
};

// Campos internos de máquina: no aportan nada al humano en ningún renderer.
export const HIDDEN_FIELDS = new Set([
    'item_identification',
    'session_id',
    'account_id',
    'bucc_id',
    'business_unit_id',
    'business_unit_ids',
    'proposal_id',
    'item_id',
    'changed_object_id',
    'platform_event_type',
    'platform_client_type',
    'old_values',
    'new_values',
    'changed_fields',
    'extra_data',
    'source',
    '_spurious',
    '_spurious_original',
    // Contexto de alertas que el backend estampa desde el 05/10 (cards xcUa4a3N y Wq8SqQV9): los
    // consume la config de cada alerta (título, stats) cuando le sirven, y si no, no son filas.
    'problem_started_on',
    'line_covers_whole_account',
    'period_from',
    'period_to',
    'period_elapsed_pct',
    // La identidad del problema y lo que decidió el gate de correo (cards DP7i8uTf y hlfD0Dgd): son
    // para agrupar y para no repetir mails, no para el usuario.
    'problem_key',
    'problem_subject',
    'emailed',
    'email_gate',
    // A quién le salió el mail (tanda «Control de líneas», 15/10): ids de usuario y cuántos son. Los usa
    // el renglón «Avisada por mail a N personas» de «Ahora»; como fila del detalle serían ids crudos.
    'recipients',
    'recipient_count',
    'no_recipients',
    // La corrida del sync que emitió la alerta (10/10): la usa el control del agrupado, no el usuario.
    'sync_run_id',
    // Cómo se avisó una regla del Centro de Alertas (`AlertRuleEmitter`): operativo, no lo que pasó.
    // Espejo de `INTERNAL_ALERT_DETAIL_FIELDS` del backend, salvo `channels`, que es un nombre genérico
    // y lo consume la config de `alert_rule` (C-ac-fe-ahora-01).
    'observed_because',
    'email_gate_observed_reason',
    'webhook_sent',
    'recipients_count',
    'rule_snoozed',
    // Un aviso que el backend vuelve a registrar con los números de después de un cambio guardado (card
    // Fnvibexi): es para no contarlo como otro disparo, no para el usuario.
    'restated_after_change',
]);

// Campos que son PORCENTAJES (el backend los guarda como número pelado).
export const PERCENT_FIELDS = new Set(['time_completion', 'budget_completion', 'completion', 'fluctuation']);

// Campos monetarios que el backend guarda como número crudo. OJO: value/expected_value/
// goal_value/vs_period_value NO van acá — dependen de la métrica y en flex/goal ya vienen
// formateados como string desde el backend (passthrough de formatMoney).
export const MONEY_FIELDS = new Set(['total_spent', 'total_budget', 'cost', 'budget', 'limit', 'daily_budget', 'available_funds']);

// Campos cuyo valor es una constante de período (LAST_7_DAYS, THIS_MONTH...).
export const PERIOD_FIELDS = new Set(['main_period', 'vs_period']);

// Campos cuyo valor es un nombre de métrica canónico ('CPA', 'Purchase Value', 'CUSTOM:<id>').
export const METRIC_FIELDS = new Set(['metric_name']);

export const VALUE_LABELS = {
    DAILY_AD_ACCOUNT_ONLY_COST: 'activity_log.value.daily_ad_account_only_cost',
    DAILY_AD_ACCOUNT_COST_AND_BUDGET: 'activity_log.value.daily_ad_account_cost_and_budget',
    DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_BUDGET: 'activity_log.value.daily_campaign_only_budget',
    DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_COST: 'activity_log.value.daily_campaign_only_cost',
    DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_COST_AND_BUDGET: 'activity_log.value.daily_campaign_cost_and_budget',
    cost: 'activity_log.value.kind_cost',
    account_budget: 'activity_log.value.kind_account_budget',
    campaign_budget: 'activity_log.value.kind_campaign_budget',
    budget_exceeded: 'activity_log.value.indicator_budget_exceeded',
    cost_vs_time: 'activity_log.value.indicator_cost_vs_time',
    goal: 'activity_log.value.indicator_goal',
    flex_indicator: 'activity_log.value.indicator_flex',
    ods_critic: 'activity_log.value.indicator_ods',
    RoasVsLastPeriod: 'activity_log.value.indicator_roas',
    AcosVsLastPeriod: 'activity_log.value.indicator_acos',
    TYPE_ODS_VS_TODAY: 'activity_log.value.ods_vs_today',
    TYPE_ODS_VS_YESTERDAY: 'activity_log.value.ods_vs_yesterday',
    TYPE_ODS_VS_LAST_7_DAYS: 'activity_log.value.ods_vs_last_7_days',
    CAMPAIGN: 'activity_log.value.item_campaign',
    ADGROUP: 'activity_log.value.item_adgroup',
    AD_ACCOUNT: 'activity_log.value.item_account',
    ACCOUNT: 'activity_log.value.item_account',
    BUSINESS_UNIT: 'activity_log.value.item_bu',
    BU: 'activity_log.value.item_bu',
    PERIODIC: 'periods.monthly',
    TEMPORARY: 'periods.temporary',
    // Estados de campaña/adgroup que llegan de las plataformas (media plan, platform changes).
    ACTIVE: 'activity_log.value.status_active',
    ENABLED: 'activity_log.value.status_active',
    PAUSED: 'activity_log.value.status_paused',
    REMOVED: 'activity_log.value.status_removed',
};

// Enums que sólo significan algo DENTRO de su campo: `OTHER` es «Otro» como modelo de conversión y
// otra cosa en cualquier otro campo, así que no pueden ir al `VALUE_LABELS` global. Las claves son
// las mismas que usa el select de Configuración (`BUSINESS_TYPE_OPTIONS` en ClientProfileForm): un
// test recorre esa lista para que las dos no se separen.
export const FIELD_VALUE_LABELS = {
    business_type: {
        ECOMMERCE: 'client_profile.business_type_ecommerce',
        LEAD_GEN: 'client_profile.business_type_lead_gen',
        BRAND_AWARENESS: 'client_profile.business_type_brand_awareness',
        APP_INSTALL: 'client_profile.business_type_app_install',
        OTHER: 'client_profile.business_type_other',
    },
};

// Niveles de alerta: badge con la paleta de estados del design system.
// OJO: en alertas 'goal' el level es la PRIORIDAD ("1".."5") y en ODS el tipo de
// comparación (TYPE_ODS_VS_*) — esos valores no matchean acá y NO deben forzarse.
export const LEVEL_BADGES = {
    CRITIC: {labelKey: 'activity_log.value.level_critic', className: '-critic'},
    BAD: {labelKey: 'activity_log.value.level_bad', className: '-bad'},
    NORMAL: {labelKey: 'activity_log.value.level_normal', className: '-normal'},
    GOOD: {labelKey: 'activity_log.value.level_good', className: '-good'},
};
